import {z} from 'zod';

const pendingCreatedConfigurationSchema = z.object({
  ownerId: z.string().uuid(),
  intentKey: z.string(),
  configurationId: z.string().optional(),
});

export type PendingCreatedConfiguration = z.infer<typeof pendingCreatedConfigurationSchema>;
export type PendingCreatedConfigurationState =
  | {status: 'none'}
  | {status: 'pending'; record: PendingCreatedConfiguration}
  | {status: 'uncertain'; record?: PendingCreatedConfiguration}
  | {status: 'completed'; record: PendingCreatedConfiguration};

type StorageReadResult =
  | {status: 'none'}
  | {status: 'invalid'}
  | {status: 'unavailable'}
  | {status: 'record'; record: PendingCreatedConfiguration};

const listeners = new Set<VoidFunction>();
const liveOwners = new Map<string, string>();
const memoryCompletedRecords = new Map<
  string,
  {record: PendingCreatedConfiguration; persisted: boolean}
>();
let revision = 0;

const notify = () => {
  revision += 1;
  listeners.forEach((listener) => listener());
};

const readStorage = (key: string): StorageReadResult => {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return {status: 'none'};
    }
    const parsed: unknown = JSON.parse(stored);
    const result = pendingCreatedConfigurationSchema.safeParse(parsed);
    return result.success ? {status: 'record', record: result.data} : {status: 'invalid'};
  } catch {
    return {status: 'unavailable'};
  }
};

export const getPendingCreatedConfigurationKey = (
  draftKey: string,
  businessCaseId?: string,
  interestId?: string
) =>
  `${draftKey}:created:${encodeURIComponent(businessCaseId ?? '')}:${encodeURIComponent(
    interestId ?? ''
  )}`;

export const pendingCreatedConfigurationStore = {
  subscribe(listener: VoidFunction) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getRevision() {
    return revision;
  },
  getServerRevision() {
    return 0;
  },
  getState(key: string): PendingCreatedConfigurationState {
    const stored = readStorage(key);
    const memoryCompletion = memoryCompletedRecords.get(key);
    if (memoryCompletion) {
      if (!memoryCompletion.persisted || stored.status !== 'none') {
        return {status: 'completed', record: memoryCompletion.record};
      }
      memoryCompletedRecords.delete(key);
    }
    if (stored.status === 'none') {
      return {status: 'none'};
    }
    if (stored.status !== 'record') {
      return {status: 'uncertain'};
    }
    if (stored.record.configurationId) {
      return {status: 'completed', record: stored.record};
    }
    return liveOwners.get(key) === stored.record.ownerId
      ? {status: 'pending', record: stored.record}
      : {status: 'uncertain', record: stored.record};
  },
  claim(key: string, record: PendingCreatedConfiguration) {
    if (readStorage(key).status !== 'none' || memoryCompletedRecords.has(key)) {
      return false;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify(record));
    } catch {
      return false;
    }
    liveOwners.set(key, record.ownerId);
    notify();
    return true;
  },
  complete(key: string, ownerId: string, intentKey: string, configurationId: string) {
    if (liveOwners.get(key) !== ownerId) {
      return 'superseded' as const;
    }
    const completed = {ownerId, intentKey, configurationId};
    memoryCompletedRecords.set(key, {record: completed, persisted: false});
    liveOwners.delete(key);
    try {
      sessionStorage.setItem(key, JSON.stringify(completed));
      memoryCompletedRecords.set(key, {record: completed, persisted: true});
      notify();
      return 'persisted' as const;
    } catch {
      notify();
      return 'memory-only' as const;
    }
  },
  markUncertain(key: string, ownerId: string) {
    if (liveOwners.get(key) !== ownerId) {
      return;
    }
    liveOwners.delete(key);
    notify();
  },
  fail(key: string, ownerId: string) {
    if (liveOwners.get(key) !== ownerId) {
      return;
    }
    liveOwners.delete(key);
    memoryCompletedRecords.delete(key);
    const stored = readStorage(key);
    if (stored.status === 'record' && stored.record.ownerId === ownerId) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // The persisted uncertain record remains visible for non-destructive recovery.
      }
    }
    notify();
  },
  clearOwned(key: string, ownerId: string) {
    const memoryCompletion = memoryCompletedRecords.get(key);
    const stored = readStorage(key);
    const storedOwnerId = stored.status === 'record' ? stored.record.ownerId : undefined;
    if (memoryCompletion?.record.ownerId !== ownerId && storedOwnerId !== ownerId) {
      return false;
    }
    liveOwners.delete(key);
    memoryCompletedRecords.delete(key);
    try {
      sessionStorage.removeItem(key);
    } catch {
      notify();
      return false;
    }
    notify();
    return true;
  },
  discard(key: string) {
    liveOwners.delete(key);
    memoryCompletedRecords.delete(key);
    try {
      sessionStorage.removeItem(key);
    } catch {
      notify();
      return false;
    }
    notify();
    return true;
  },
};
